import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import RightSidebar from './RightSidebar';
import NewPartnerForm from './NewPartnerForm';
import { PlusCircle, Search, Download, Copy, Check } from 'lucide-react';
import CryptoJS from 'crypto-js';
import Select from "react-select";

const JUSPAY_CLIENT_ID = 'snapecab';
const JUSPAY_RETURN_URL = 'https://snapecabs.com';

const pad2 = n => n < 10 ? `0${n}` : `${n}`;
// Helper to get expires_at in 'YYYY-MM-DD HH:mm:ss'
function getExpiryDate() {
  const d = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

// Helper to get today's date in YYYY-MM-DD format
function getTodayDate() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function extractPaymentLink(data) {
  if (!data || typeof data !== 'object') return null;
  return (
    data.juspay?.payment_links?.web ||
    data.juspay?.web ||
    data.payment_links?.web ||
    data.web ||
    data.payment_link ||
    null
  );
}

// PartnerRowSidebar component with payment link and WhatsApp features
const PartnerRowSidebar = ({ partner, onUpdate, rms = new Map() }) => {
  const [amount, setAmount] = useState('');
  const [customerPhone] = useState(partner.partner_number || '');
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState(null);
  const [paymentLink, setPaymentLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendResponseMsg, setSendResponseMsg] = useState('');

  // State for status toggle functionality
  const [isActive, setIsActive] = useState(partner.activity_status);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [updateStatusMsg, setUpdateStatusMsg] = useState('');

  // --- NEW STATE for RM Assignment ---
  const [selectedRm, setSelectedRm] = useState(partner.rm_id || '');
  const [isAssigningRm, setIsAssigningRm] = useState(false);
  const [assignRmMsg, setAssignRmMsg] = useState('');

  const [vehicles, setVehicles] = useState([]);
  const [unassignedVehicles, setUnassignedVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  // vehicle selection for WhatsApp moved BEFORE generating link
  const [selectedVehicleForPayment, setSelectedVehicleForPayment] = useState('');

  // NEW — state for odometer reading
  const [handOverOdometer, setHandOverOdometer] = useState('');
  const [depositPaid, setDepositPaid] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');

  useEffect(() => {
    async function fetchVehicles() {
      try {
        const res = await api.get(`/vehicles/partner/${partner.id}`);
        const partnerVehicles = res.data.vehicles || [];
        setVehicles(partnerVehicles);
        if (partnerVehicles.length > 0) {
          setSelectedVehicleForPayment(partnerVehicles[0].vehicle_no);
        } else {
          setSelectedVehicleForPayment('');
        }
      } catch (err) {
        console.error("Failed to fetch vehicles", err);
        setVehicles([]);
      }
    }
    if (partner.id) {
      fetchVehicles();
    }
  }, [partner.id]);

  // Reset state when a different partner is selected
  useEffect(() => {
    setIsActive(partner.activity_status);
    setSelectedRm(partner.rm_id || '');
    setAssignRmMsg('');
  }, [partner]);

  // Effect to reset local state when a new partner is selected
  useEffect(() => {
    setIsActive(partner.activity_status);
    setUpdateStatusMsg('');
  }, [partner]);

  useEffect(() => {
    async function fetchUnassigned() {
      try {
        const res = await api.get(`/vehicles/unassigned`);
        setUnassignedVehicles(res.data || []);
      } catch (err) {
        console.error("Failed to fetch unassigned vehicles", err);
        setUnassignedVehicles([]);
      }
    }
    fetchUnassigned();
  }, []);

  // --- NEW HANDLER for Assigning RM ---
  const handleAssignRm = async () => {
    if (!selectedRm) {
      setAssignRmMsg('Please select an RM.');
      return;
    }
    setIsAssigningRm(true);
    setAssignRmMsg('');
    try {
      await api.patch(`/partners/${partner.id}/rm`, { rmId: selectedRm });
      setAssignRmMsg('RM assigned successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to assign RM:", error);
      setAssignRmMsg('Error: Could not assign RM.');
    } finally {
      setIsAssigningRm(false);
    }
  };

  const handleAssignVehicle = async () => {
    if (!selectedVehicleId ||
      handOverOdometer === "" ||
      Number(handOverOdometer) < 0 ||
      paymentMode === "" ||
      (paymentMode === "online" && !transactionId)) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const vehicleToAssign = unassignedVehicles.find(v => v.id === selectedVehicleId);
      const handoverPayload = {
        partner_id: partner.id,
        vehicle_no: vehicleToAssign.vehicle_no,
        handover_reading: Number(handOverOdometer),
        deposit_received: Number(depositPaid) || null,
        payment_mode: paymentMode,
        txn_id: paymentMode === 'online' ? transactionId : null
      };

      await api.post('/vehicles/assign-with-handover', {
        vehicleId: selectedVehicleId,
        partnerId: partner.id,
        handoverData: handoverPayload
      });

      const [vehiclesRes, unassignedRes] = await Promise.all([
        api.get(`/vehicles/partner/${partner.id}`),
        api.get('/vehicles/unassigned')
      ]);

      setVehicles(vehiclesRes.data.vehicles || []);
      setUnassignedVehicles(unassignedRes.data || []);

      setSelectedVehicleId("");
      setHandOverOdometer("");
      setDepositPaid("");
      setPaymentMode("");
      setTransactionId("");

      alert("Vehicle assigned and handover logged successfully!");
    } catch (err) {
      console.error("Failed to assign vehicle", err);
      alert("Failed to assign vehicle: " + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleStatus = async () => {
    setIsUpdatingStatus(true);
    setUpdateStatusMsg('');
    const newStatus = !isActive;

    try {
      await api.patch(`/partners/${partner.id}/status`, { active: newStatus });
      setIsActive(newStatus);
      setUpdateStatusMsg('Status updated successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
      setUpdateStatusMsg('Error: Could not update status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  useEffect(() => {
    async function fetchLastPaymentLink() {
      setResponseMsg(null);
      setSendResponseMsg('');
      setPaymentLink('');
      setLinkCopied(false);
      try {
        if (!customerPhone) {
          setResponseMsg('Partner phone not available to fetch link.');
          return;
        }
        const today = getTodayDate();
        const response = await fetch(`/api/payments/latest-link/${customerPhone}?date=${today}`);
        if (!response.ok) {
          setResponseMsg('No payment link found for today.');
          return;
        }
        const data = await response.json();
        const link = extractPaymentLink(data);
        if (link) {
          setPaymentLink(link);
        } else {
          setResponseMsg('No payment link found for today.');
        }
      } catch (error) {
        setResponseMsg('Error fetching payment link.');
      }
    }
    fetchLastPaymentLink();
  }, [customerPhone]);

  const generateOrderId = () => {
    const raw = `LP_${partner.id}_${Date.now()}`;
    const md5Hash = CryptoJS.MD5(raw).toString();
    return md5Hash.substring(0, 16);
  };

  const handleGenerateLink = async () => {
    if (!amount || !customerPhone || !selectedVehicleForPayment) {
      alert('Please enter amount, phone number and select a vehicle');
      return;
    }

    setLoading(true);
    setResponseMsg(null);
    setSendResponseMsg('');
    setPaymentLink('');
    setLinkCopied(false);

    try {
      const payload = {
        driver_id: partner.id,
        phone: customerPhone,
        amount: +amount,
        order_id: generateOrderId(),
        vehicle_no: selectedVehicleForPayment,
      };

      const response = await api.post('/payments/create-payment-link', payload);
      const link = extractPaymentLink(response.data);

      if (link) {
        setPaymentLink(link);
        setLinkCopied(false);
        setResponseMsg('Payment link generated successfully!');
      } else {
        setResponseMsg('Payment link not found in response.');
      }
    } catch (error) {
      setResponseMsg(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!paymentLink) return;
    try {
      await navigator.clipboard.writeText(paymentLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = paymentLink;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!paymentLink) {
      alert('Generate or select a payment link first.');
      return;
    }
    if (!selectedVehicleForPayment) {
      alert('Please select a vehicle.');
      return;
    }

    setSendingWhatsApp(true);
    setSendResponseMsg('');

    try {
      const sendPayload = {
        driver_id: partner.id,
        phone_number: customerPhone,
        partner_name: partner.partner_name,
        vehicle_number: selectedVehicleForPayment,
        email: partner.email || '',
        amount_due: +amount,
        payment_link: paymentLink,
      };

      await api.post('/messages/send-whatsapp', sendPayload);

      setSendResponseMsg('WhatsApp message sent successfully!');
    } catch (error) {
      setSendResponseMsg(`Error sending WhatsApp message: ${error.message}`);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Manage Status */}
      <div>
        <h4 className="text-lg font-semibold text-neutral-800 dark:text-white mb-3">Manage Status</h4>
        <div className="flex items-center justify-between p-4 bg-neutral-100 dark:bg-neutral-700/50 rounded-lg">
          <label className="font-semibold text-neutral-600 dark:text-neutral-300">
            {isActive ? 'Active' : 'Inactive'}
          </label>
          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={isUpdatingStatus}
            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 ${isActive ? 'bg-green-500' : 'bg-red-500'}`}
          >
            <span
              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>
        {isUpdatingStatus && <p className="mt-2 text-sm text-blue-500">Updating...</p>}
        {updateStatusMsg && (
          <p className={`mt-2 text-sm ${updateStatusMsg.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
            {updateStatusMsg}
          </p>
        )}
      </div>

      {/* RM assignment */}
      <div>
        <h4 className="text-lg font-semibold text-neutral-800 dark:text-white mb-3">Assign Relationship Manager</h4>
        <div className="space-y-2">
          <select
            value={selectedRm}
            onChange={(e) => setSelectedRm(e.target.value)}
            className="w-full pl-3 pr-10 py-2 text-base border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            disabled={isAssigningRm}
          >
            <option value="">Select an RM</option>
            {Array.from(rms.entries()).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <button
            onClick={handleAssignRm}
            disabled={isAssigningRm}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isAssigningRm ? 'Assigning...' : 'Assign RM'}
          </button>
        </div>
        {assignRmMsg && (
          <p className={`mt-2 text-sm ${assignRmMsg.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
            {assignRmMsg}
          </p>
        )}
      </div>

      {/* Payment Section */}
      <div className="flex flex-col gap-4">
        <div>
          <label className="font-bold block mb-2">Payment Section for {partner.partner_name}:</label>
          <input
            type="text"
            className="border border-neutral-300 rounded-lg px-3 py-2 w-full"
            placeholder="Enter the amount"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            disabled={loading || sendingWhatsApp}
          />
          <input
            type="tel"
            className="border border-neutral-300 rounded-lg px-3 py-2 w-full bg-gray-100"
            placeholder="Phone not available"
            value={customerPhone}
            readOnly
          />

          {/* Vehicle dropdown moved BEFORE generate link */}
          <div className="mt-2">
            <label className="font-bold block mb-2">Select Vehicle:</label>
            <select
              value={selectedVehicleForPayment}
              onChange={e => setSelectedVehicleForPayment(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 w-full"
              disabled={sendingWhatsApp || loading}
            >
              <option value="">Select Vehicle</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.vehicle_no}>
                  {v.vehicle_no}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleGenerateLink}
            disabled={loading || sendingWhatsApp}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg mt-2"
          >
            {loading ? 'Generating...' : 'Generate Payment Link'}
          </button>

          {responseMsg && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{responseMsg}</p>
          )}

          {paymentLink && (
            <div className="mt-3 p-3 bg-neutral-100 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
              <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2 block">
                Payment link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={paymentLink}
                  className="flex-1 min-w-0 border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100"
                />
                <button
                  type="button"
                  onClick={handleCopyPaymentLink}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-neutral-700 hover:bg-neutral-800 dark:bg-neutral-600 dark:hover:bg-neutral-500"
                  title="Copy payment link"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
              >
                Open payment page
              </a>
            </div>
          )}

          {paymentLink && (
            <>
              <button
                onClick={handleSendWhatsApp}
                disabled={sendingWhatsApp || loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg mt-2"
              >
                {sendingWhatsApp ? 'Sending...' : 'Send to WhatsApp'}
              </button>
              {sendResponseMsg && (
                <p className="mt-2 text-sm text-green-700 dark:text-green-300">{sendResponseMsg}</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Vehicle Details */}
      <h4 className="text-lg font-semibold mb-3">Vehicle Details</h4>
      {vehicles.length > 0 ? vehicles.map((v) => (
        <div key={v.id} className="p-4 bg-neutral-100 rounded-lg mb-1">
          <p><strong>Vehicle No:</strong> {v.vehicle_no}</p>
          {v.make && <p><strong>Make:</strong> {v.make}</p>}
          {v.model && <p><strong>Model:</strong> {v.model}</p>}
          {v.chassis_number && <p><strong>Chassis No:</strong> {v.chassis_number}</p>}
        </div>
      )) : <p></p>}

      <div className="mt-3">
        <label className="block mb-2">Assign another vehicle:</label>
        <div className="space-y-4">
          <div className="flex gap-2">
            {/* Vehicle Dropdown */}
            <Select
              options={unassignedVehicles.map((v) => ({
                value: v.id,
                label: v.vehicle_no
              }))}
              value={
                selectedVehicleId
                  ? {
                    value: selectedVehicleId,
                    label: unassignedVehicles.find(v => v.id === selectedVehicleId)?.vehicle_no || ""
                  }
                  : null
              }
              onChange={(selectedOption) =>
                setSelectedVehicleId(selectedOption ? selectedOption.value : "")
              }
              placeholder="Search or select vehicle..."
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: "250px",
                  width: "100%",
                }),
                input: (base) => ({
                  ...base,
                  minWidth: "200px",
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />

            {/* Odometer Input */}
            <input
              type="number"
              min="0"
              placeholder="Odometer Reading"
              value={handOverOdometer}
              onChange={(e) => setHandOverOdometer(e.target.value)}
              className="border rounded-lg p-2 w-1/2"
            />
          </div>

          {/* Security Deposit Section */}
          <div className="p-4 border rounded-lg bg-neutral-50">
            <h4 className="text-md font-semibold mb-3">Security Deposit</h4>

            {/* Fixed Security Deposit */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Total Security Deposit</label>
              <input
                type="number"
                value={25000}
                disabled
                className="border rounded-lg p-2 w-full bg-gray-100"
              />
            </div>

            {/* Amount Paid */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Amount Paid by Driver Partner</label>
              <input
                type="number"
                min="0"
                max="25000"
                value={depositPaid}
                onChange={(e) => setDepositPaid(e.target.value)}
                className="border rounded-lg p-2 w-full"
              />
              {depositPaid < 25000 && (
                <p className="text-xs text-red-500 mt-1">
                  Remaining: ₹{25000 - (Number(depositPaid) || 0)}
                </p>
              )}
            </div>

            {/* Mode of Payment */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Mode of Payment</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="border rounded-lg p-2 w-full"
              >
                <option value="">-- Select Mode --</option>
                <option value="cash">Cash</option>
                <option value="online">Online</option>
              </select>
            </div>

            {/* Transaction ID (only if online) */}
            {paymentMode === 'online' && (
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="border rounded-lg p-2 w-full"
                />
              </div>
            )}
          </div>

          {/* Assign Button AFTER security/payment info */}
          <div className="flex">
            <button
              onClick={handleAssignVehicle}
              disabled={
                !selectedVehicleId ||
                handOverOdometer === "" ||
                Number(handOverOdometer) < 0 ||
                paymentMode === "" ||
                (paymentMode === "online" && !transactionId)
              }
              className={`
                py-2 px-4 rounded-lg text-white w-full
                ${!selectedVehicleId ||
                  handOverOdometer === "" ||
                  Number(handOverOdometer) < 0 ||
                  paymentMode === "" ||
                  (paymentMode === "online" && !transactionId)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
                }
              `}
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// MODIFIED: Component to display the table of partners WITH status filter
const PartnerTable = ({
  partners,
  rms,
  onAddPartner,
  totalPartners,
  isLoading,
  searchTerm,
  setSearchTerm,
  statusFilter,          // NEW
  setStatusFilter,       // NEW
  onPartnerClick,
  onDownloadCsv
}) => (
  <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-800 dark:text-white">
          Partners
        </h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          A total of {totalPartners} partners.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search by name or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* NEW: Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="py-2 px-3 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {/* Action Buttons */}
        <button
          onClick={onDownloadCsv}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        >
          <Download className="w-5 h-5" />
          <span>Download CSV</span>
        </button>
        <button
          onClick={onAddPartner}
          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        >
          <PlusCircle className="w-5 h-5" />
          <span>Onboard Partner</span>
        </button>
      </div>
    </div>
    <div className="overflow-x-auto">
      {isLoading ? (
        <div className="p-8 text-center text-neutral-500">Loading partners...</div>
      ) : (
        <table className="min-w-full leading-normal">
          <thead>
            <tr className="border-b-2 border-neutral-200 dark:border-neutral-700">
              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Onboarding Date
              </th>
              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Partner ID
              </th>
              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Partner Name
              </th>
              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Partner Number
              </th>
              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Status
              </th>

              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Assigned RM
              </th>

              <th className="px-5 py-3 bg-neutral-100 dark:bg-neutral-900 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => (
              <tr
                key={partner.id}
                className="hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors duration-200 border-b border-neutral-200 dark:border-neutral-700"
              >
                <td className="px-5 py-5 text-sm">
                  <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                    {partner.onboarding_date
                      ? new Date(partner.onboarding_date).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </td>
                <td className="px-5 py-5 text-sm">
                  <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                    {partner.id}
                  </p>
                </td>
                <td className="px-5 py-5 text-sm">
                  <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                    {partner.partner_name}
                  </p>
                </td>
                <td className="px-5 py-5 text-sm">
                  <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                    {partner.partner_number || 'N/A'}
                  </p>
                </td>
                <td className="px-5 py-5 text-sm">
                  <span className={`relative inline-block px-3 py-1 font-semibold leading-tight rounded-full ${partner.activity_status ? 'text-green-900 bg-green-200' : 'text-red-900 bg-red-200'}`}>
                    {partner.activity_status ? 'Active' : 'Inactive'}
                  </span>
                </td>

                <td className="px-5 py-5 text-sm">
                  <p className="text-neutral-900 dark:text-white whitespace-no-wrap">
                    {rms.get(partner.rm_id) || 'N/A'}
                  </p>
                </td>
                <td className="px-5 py-5 text-sm">
                  <button
                    onClick={() => onPartnerClick(partner)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);


const PartnerOnboardingView = () => {
  const [partners, setPartners] = useState([]);
  const [rms, setRms] = useState(new Map());
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarContent, setSidebarContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarForNewPartner, setSidebarForNewPartner] = useState(false);

  // NEW: Status filter state
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [partnersResponse, rmsResponse] = await Promise.all([
        api.get('/partners'),
        api.get('/rm/rms'),
      ]);
      // ENSURE ARRAY to avoid .filter is not a function
      setPartners(Array.isArray(partnersResponse.data?.partners) ? partnersResponse.data.partners : []);
      const rmData = rmsResponse.data || [];
      const rmMap = new Map(rmData.map((rm) => [rm.id, rm.name]));
      setRms(rmMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      setPartners([]); // fallback to an empty array
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePartnerAdded = () => {
    fetchData();
    setSidebarOpen(false);
    setSidebarForNewPartner(false);
  };

  const handleAddPartnerClick = () => {
    setSidebarContent(<NewPartnerForm onPartnerAdded={handlePartnerAdded} />);
    setSidebarOpen(true);
    setSidebarForNewPartner(true);
  };

  const handlePartnerClick = async (partner) => {
    try {
      // Fetch partner with payments from backend
      const resp = await api.get(`/partners/${partner.id}/with-payments`);
      const fullPartner = resp.data;

      // Pass enriched partner data with payment_summary to sidebar
      setSidebarContent(
        <PartnerRowSidebar
          partner={fullPartner}
          onUpdate={fetchData}
          rms={rms}
        />
      );
      setSidebarOpen(true);
      setSidebarForNewPartner(false);
    } catch (error) {
      console.error('Failed to fetch partner details with payments:', error);
      // fallback: open sidebar with original data
      setSidebarContent(
        <PartnerRowSidebar
          partner={partner}
          onUpdate={fetchData}
          rms={rms}
        />
      );
      setSidebarOpen(true);
      setSidebarForNewPartner(false);
    }
  };

  // Filter logic for search term + status
  const filteredPartners = useMemo(() => {
    const source = Array.isArray(partners) ? partners : []; // guard
    return source.filter(partner => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        (partner.partner_name || '').toLowerCase().includes(searchLower) ||
        (partner.partner_number || '').includes(searchTerm);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && !!partner.activity_status) ||
        (statusFilter === 'inactive' && !partner.activity_status);

      return matchesSearch && matchesStatus;
    });
  }, [partners, searchTerm, statusFilter]);

  // CSV Download (kept as full partners list as in your original)
  const handleDownloadCsv = () => {
    if (!Array.isArray(partners) || partners.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = ['Partner Name', 'Partner Number','Partner ID','Status', 'Assigned Vehicle', 'Assigned RM'];

    const formatCsvCell = (cellData) => {
      const stringData = String(cellData ?? '');
      if (stringData.includes(',') || stringData.includes('"') || stringData.includes('\n')) {
        return `"${stringData.replace(/"/g, '""')}"`;
      }
      return stringData;
    };

    const csvRows = [
      headers.join(','),
      ...partners.map(partner => [
        formatCsvCell(partner.partner_name),
        formatCsvCell(partner.partner_number || 'N/A'),
        formatCsvCell(partner.id),
        formatCsvCell(partner.activity_status ? 'Active' : 'Inactive'),
        formatCsvCell(partner.vehicle_no || 'Not Assigned'),
        formatCsvCell(rms.get(partner.rm_id) || 'N/A')
      ].join(','))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `partners-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      <PartnerTable
        partners={filteredPartners}
        rms={rms}
        onAddPartner={handleAddPartnerClick}
        totalPartners={Array.isArray(partners) ? partners.length : 0}
        isLoading={isLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}          // NEW
        setStatusFilter={setStatusFilter}    // NEW
        onPartnerClick={handlePartnerClick}
        onDownloadCsv={handleDownloadCsv}
      />
      <RightSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setSidebarContent(null);
          setSidebarForNewPartner(false);
        }}
      >
        {sidebarContent}
      </RightSidebar>
    </div>
  );
};

export default PartnerOnboardingView;